import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import style from './date-field.module.scss';

const POPOVER_HEIGHT_ESTIMATE = 440;

/**
 * DateField — single-date picker matching DateRangeField's visual
 * language. Trigger is a 48px pill matching PLInput; popover uses
 * the same calendar styling (soft in-range band, rounded pill
 * selection, muted weekends, custom chevron nav). Accepts `minDate`
 * so past / buffer-window dates can be disabled.
 *
 * Staged selection model: picking a date stages it in the popover.
 * User confirms via Apply or discards via Cancel — same pattern as
 * DateRangeField, keeps preview/form state stable during exploration.
 */
function DateField({
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  compact = false,
}) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');

  // Staged draft — only committed on Apply
  const [draft, setDraft] = useState(value || null);

  // Re-sync draft whenever the popover opens (external resets + edit)
  useEffect(() => {
    if (open) setDraft(value || null);
  }, [open, value]);

  // Close on outside click + ESC
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Return focus to trigger on close
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open && triggerRef.current) {
      triggerRef.current.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Smart placement (same as DateRangeField)
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }
  }, [open]);

  const toStr = d => (d ? format(d, 'yyyy-MM-dd') : null);
  const selectedDate = draft ? new Date(draft) : null;
  const committed = value ? new Date(value) : null;

  const handleSelect = d => setDraft(toStr(d));
  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };
  const handleCancel = () => {
    setDraft(value || null);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className={`${style.trigger} ${compact ? style.compact : ''} ${open ? style.open : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={style.value}>
          {committed ? (
            <span>{format(committed, 'MMM d, yyyy')}</span>
          ) : (
            <span className={style.placeholder}>{placeholder}</span>
          )}
        </span>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className={style.icon} src="/images/calendar.svg" alt="" aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`${style.popover} ${placement === 'top' ? style.popoverTop : ''}`}
          role="dialog"
          aria-label="Date picker"
        >
          <div className={style.calendarWrap}>
            <div className={style.headerRow}>
              <span className={style.headerTitle}>Pick a date</span>
              <span className={style.headerValue}>
                {selectedDate ? format(selectedDate, 'MMM d, yyyy') : '—'}
              </span>
            </div>
            <DatePicker
              selected={selectedDate}
              onChange={handleSelect}
              inline
              minDate={minDate}
              maxDate={maxDate}
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className={style.customMonthHeader}>
                  <button
                    type="button"
                    className={style.monthNav}
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    aria-label="Previous month"
                  >
                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path
                        d="M7.5 3L4.5 6L7.5 9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <span className={style.monthTitle}>{format(monthDate, 'MMMM yyyy')}</span>
                  <button
                    type="button"
                    className={`${style.monthNav} ${style.monthNavRight}`}
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    aria-label="Next month"
                  >
                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path
                        d="M4.5 3L7.5 6L4.5 9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            />
            <div className={style.footer}>
              <button type="button" className={style.btnCancel} onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="button"
                className={style.btnApply}
                onClick={handleApply}
                disabled={!draft}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  compact: PropTypes.bool,
};

export default DateField;
